import _get from "lodash/get";
import _camelCase from "lodash/camelCase";
import DOMPurify from "dompurify";
import { fixValue } from "../../util/format";

/**
 * This file contains redux selectors for accessing template related data
 */

/**
 * Return the template for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getTemplate = state => {
    return _get(state, "newsletters.currentData.data.groupingTemplate", {});
};

/**
 * Return a property from the template for the current newsletter
 * @param {*} state
 * @param {*} property
 * @param {*} default
 * @returns {*}
 */
export const getTemplateProperty = (state, property, def = null) => {
    const template = getTemplate(state);
    return _get(template, property, def);
};

/**
 * Compile an object that will be passed as a style property to a react object
 * based on a template property from the current newsletter
 * @param state
 * @param property
 */
export const getReactStyleObjectForProperty = (state, property) => {
    const styleObject = getTemplateProperty(state, property, {});
    const isCustomFormat = getNewsletterUseCustomFormat(state);
    const { hex, fontHex, fontSize } = styleObject;
    const fontFamily = getNewsletterFontFamily(state);
    const styling = _get(styleObject, "styling", []);
    const color = hex || fontHex;

    const styleShards = styling.reduce((result, shard) => {
        const [key, value] = shard.split(":");
        result[_camelCase(key)] = value;
        return result;
    }, {});

    const result = {
        ...styleShards,
        color: color,
    };

    if (isCustomFormat) {
        result.fontSize = `${fontSize}px`;
        result.fontFamily = fontFamily;
    }

    return result;
};

/**
 * Return the react style object for section headers
 * @param state
 * @returns {*}
 */
export const getReactStyleSectionHeader = state => {
    return getReactStyleObjectForProperty(state, "section.style");
};

/**
 * Return the react style object for the legal statement
 * @param state
 * @returns {*}
 */
export const getReactStyleLegalStatement = state => {
    return getReactStyleObjectForProperty(state, "header.legalStatement");
};

/**
 * Return the react style object for the article text
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleText = state => {
    return getReactStyleObjectForProperty(state, "article.textFormat");
};

/**
 * Return the react style object for the article titles
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleTitles = state => {
    return getReactStyleObjectForProperty(state, "article.titleFormat");
};

/**
 * Return the react style object for the article dates
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleDates = state => {
    return getReactStyleObjectForProperty(state, "article.dateFormat");
};

/**
 * Return the react style object for the article authors
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleAuthors = state => {
    return getReactStyleObjectForProperty(state, "article.authorFormat");
};

/**
 * Should the author be displayed for articles? Configured on AN07b
 * @param state
 * @returns {*}
 */
export const getShouldShowAuthor = state => {
    return getTemplateProperty(state, "article.authorFormat.include") !== "false";
};



/**
 * Return the react style object for the article outlets
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleOutlets = state => {
    return getReactStyleObjectForProperty(state, "article.outletFormat");
};

/**
 * Return the react style object for the article metric label
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleMetricLabel = state => {
    return getReactStyleObjectForProperty(state, "misc.dataLabelFormat");
};

/**
 * Return the react style object for the article metric value
 * @param state
 * @returns {*}
 */
export const getReactStyleArticleMetricValue = state => {
    return getReactStyleObjectForProperty(state, "misc.metricFormat");
};

/**
 * Return the Email Background Color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getNewsletterEmailBackgroundColor = state => {
    return getTemplateProperty(state, "core.emailBackgroundHex");
};

/**
 * Return the Accent Color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getNewsletterAccentColor = state => {
    return getTemplateProperty(state, "core.accentColorHex", "rgb(255, 85, 0)");
};

/**
 * Return the Content Background Color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getNewsletterContentBackgroundColor = state => {
    return getTemplateProperty(state, "core.contentBackgroundHex");
};

/**
 * Return the Overall font family from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getNewsletterFontFamily = state => {
    return getTemplateProperty(state, "core.newsletterFont");
};

/**
 * Return whether we are following custom fonts (based on the template for the current newsletter)
 * @param {*} state
 * @returns {boolean}
 */
export const getNewsletterUseCustomFormat = state => {
    return getTemplateProperty(state, "core.interactiveFont") == "customFont";
};

/**
 * Return the header interactive button text from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderInteractiveVersionButtonText = state => {
    return getTemplateProperty(state, "header.interactiveVersion.button.text");
};

/**
 * Return the header interactive link text from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderInteractiveVersionLinkText = state => {
    return getTemplateProperty(
        state,
        "header.interactiveVersion.hyperlink.text"
    );
};

/**
 * Return the header break line color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderBreakLineColor = state => {
    return getTemplateProperty(state, "header.breakLine.hex");
};

/**
 * Return the Newsletter Display Name from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getNewsletterDisplayName = state => {
    return getTemplateProperty(state, "header.displayName.text");
};

/**
 * Return the Header Legal Statement Label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderLegalStatementLabel = state => {
    return getTemplateProperty(state, "header.legalStatement.label");
};

/**
 * Return the Header Custom Text Label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderCustomTextLabel = state => {
    return getTemplateProperty(state, "header.customText.label");
};

/**
 * Return the Header Custom Text Font Size from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderCustomTextFontSize = state => {
    return getTemplateProperty(state, "header.customText.fontSize");
};

/**
 * Return the Header Custom Text from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderCustomText = state => {
    return DOMPurify.sanitize(
        getTemplateProperty(state, "header.customText.text", "")
    );
};

/**
 * Return the Header Custom Images Label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getHeaderCustomImagesLabel = state => {
    return getTemplateProperty(state, "header.customImages.label");
};

/**
 * Return the "Top Stories" label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getTopStoriesLabel = state => {
    return getTemplateProperty(state, "section.topStories.label");
};

/**
 * Return the "Top Syndicates By Sharing" label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getTopSyndicatesBySharingLabel = state => {
    return getTemplateProperty(
        state,
        "misc.syndication.listTopSyndicatesBySharing.label"
    );
};

/**
 * Return the "Top Syndicates By Impressions" label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getTopSyndicatesByImpressionsLabel = state => {
    return getTemplateProperty(
        state,
        "misc.syndication.listTopSyndicatesByImpressions.label"
    );
};

/**
 * Return the "Table of Contents" label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getTableOfContentsLabel = state => {
    return getTemplateProperty(state, "section.toc.label");
};

/**
 * Return the "Section Buttons" Background Color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getSectionButtonsBackgroundColor = state => {
    return getTemplateProperty(state, "section.toc.btnHex");
};

/**
 * Return the "Section Buttons" Font Color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getSectionButtonsColor = state => {
    return getTemplateProperty(state, "section.toc.btnFontHex");
};

/**
 * Return the "Section Buttons" Font Size from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getSectionButtonsFontSize = state => {
    return getTemplateProperty(state, "section.toc.btnFontSize");
};

/**
 * Return the "Section Buttons" Label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getSectionButtonsLabel = state => {
    return getTemplateProperty(state, "section.toc.btnLabel");
};

/**
 * Return the "Section Break Lines" Color from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getSectionBreakLinesColor = state => {
    return getTemplateProperty(state, "section.breakLines.hex");
};

/**
 * Return the "newsletter header image" URL from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getNewsletterHeaderImage = state => {
    return _get(state, "newsletters.currentData.data.headerImage");
};

/**
 * Should Include the newsletter name in the interactive version
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderNewsletterNameInteractive = state => {
    return (
        getTemplateProperty(state, "core.includeNameInInteractive") == "true"
    );
};

/**
 * Should Include the newsletter banner in the interactive version
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderNewsletterBannerInteractive = state => {
    return (
        getTemplateProperty(state, "core.includeBannerInInteractive") == "true"
    );
};

/**
 * Should Include the newsletter date in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderNewsletterDate = state => {
    return (
        getTemplateProperty(state, "header.newsletterDate.include") == "true"
    );
};

/**
 * Should Include the interactive button in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderInteractiveButton = state => {
    return (
        getTemplateProperty(state, "header.interactiveVersion.include") ==
        "button"
    );
};

/**
 * Should Include the interactive link in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderInteractiveLink = state => {
    return (
        getTemplateProperty(state, "header.interactiveVersion.include") ==
        "hyperlink"
    );
};

/**
 * US6702 Should Include Audio Reader Link
 * @param state
 * @returns {boolean}
 */
export const shouldIncludeAudioReaderLink = state => {
    return getTemplateProperty(state, "core.audioReader.enabled") === "true";
};

/**
 * Should Include the break line in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderBreakLine = state => {
    return getTemplateProperty(state, "header.breakLine.include") == "true";
};

/**
 * Should Include logo in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderLogo = state => {
    return getTemplateProperty(state, "header.image.include") == "logo";
};

/**
 * Should Include banner in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderBanner = state => {
    return getTemplateProperty(state, "header.image.include") == "banner";
};

/**
 * Should Include newsletter name  in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderDisplayName = state => {
    return getTemplateProperty(state, "header.displayName.include") == "true";
};

/**
 * Should Include legal statement in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderLegalStatement = (state, position) => {
    const targetPosition = getLegalStatementPosition(state);

    return (
        getTemplateProperty(state, "header.legalStatement.include") == "true" &&
        targetPosition === position
    );
};

/**
 * the legal statement might be output at top of page or below the full text section
 * @param {*} state
 * @returns {String} - "top" || "bottom", default "top"
 */
export const getLegalStatementPosition = state => {
    let position = getTemplateProperty(state, "header.legalStatement.position");

    if (!position) {
        position = "top";
    }

    return position;
};

/**
 * Should Include custom text in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderCustomText = state => {
    return getTemplateProperty(state, "header.customText.include") == "true";
};

/**
 * Should Include custom images in the header from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeHeaderCustomImages = state => {
    return getTemplateProperty(state, "header.customImages.include") == "true";
};

/**
 * Should Include "Top Stories" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeTopStories = state => {
    return getTemplateProperty(state, "section.topStories.include") == "true";
};

/**
 * Should Include "Table of Contents" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeTableOfContents = state => {
    return getTemplateProperty(state, "section.toc.include") == "toc";
};

/**
 * Should Include "Table of Contents" Interactive Link from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeTableOfContentsInteractiveLink = state => {
    const value = getTemplateProperty(
        state,
        "section.toc.includeInteractiveVersionLink",
        "false"
    );
    return value == "true";
};

/**
 * Should Include "Section Buttons" Interactive Link from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeSectionButtonsInteractiveLink = state => {
    const value = getTemplateProperty(
        state,
        "section.toc.btnIncludeInteractiveVersionLink",
        "false"
    );
    return value == "true";
};

/**
 * Should Include "Further Reading" Articles from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeFurtherReadingArticles = state => {
    const value = getTemplateProperty(
        state,
        "section.toc.includeFurtherReadingArticles",
        "false"
    );
    return value == "true";
};

/**
 * Should Include "Section Buttons" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeSectionButtons = state => {
    return getTemplateProperty(state, "section.toc.include") == "buttons";
};

/**
 * Should Include "Section Break Lines" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeSectionBreakLines = state => {
    return getTemplateProperty(state, "section.breakLines.include") == "true";
};

/**
 * Should Include "Section Article Count" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeSectionArticleCount = state => {
    return getTemplateProperty(state, "section.articleCount.include") == "true";
};

/**
 * Should Include "Outlet City State" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeOutletCityState = state => {
    return (
        getTemplateProperty(state, "article.includeOutletCityState") == "true"
    );
};

/**
 * Should Include "Article Images" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleImages = state => {
    return getTemplateProperty(state, "core.articleImages.include") == "true";
};

/**
 * Should Include "Blurb Only" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeBlurbOnly = state => {
    return (
        getTemplateProperty(state, "article.fullText.include") == "blurbOnly"
    );
};

/**
 * Should Include "Full Text Interactive" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeFullTextInteractive = state => {
    return (
        getTemplateProperty(state, "article.fullText.include") ==
        "fullTextInteractive"
    );
};

/**
 * Should Include "Full Text Email" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeFullTextEmail = state => {
    return (
        getTemplateProperty(state, "article.fullText.include") ==
        "fullTextEmail"
    );
};

/**
 * Is the newsletter configured to show full text
 * @param {*} state
 * @returns {boolean}
 */
export const shouldShowFullText = state => {
    return (
        shouldIncludeFullTextInteractive(state) ||
        shouldIncludeFullTextEmail(state)
    );
};

// /**
//  * Are any metrics enabled
//  * @param state
//  */
// export const anyMetricsEnabled = (state) => {
//     return shouldIncludeArticleToneIcon(state) || shouldIncludeArticleToneText(state) ||
//         shouldIncludeArticleSignificance(state) ||
//         shouldIncludeArticlePrimaryOutletImpressions(state) || shouldIncludeArticleGroupImpressions(state) ||
//         shouldIncludeArticleSyndication(state) || shouldIncludeArticleTotalShares(state) || shouldIncludeArticleSharesByType(state) ||
//         shouldIncludeRetweets(state)
// };

/**
 * Should Include "Full Text Interactive Version" Link from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeFullTextInteractiveVersionLink = state => {
    const value = getTemplateProperty(
        state,
        "article.fullText.linkInteractiveVersion",
        "false"
    );
    return value == "true";
};

/**
 * Should Include "Translated NELI Content" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeTranslatedNeliContent = state => {
    return getTemplateProperty(state, "article.translateNeliContent") == "true";
};

/**
 * Should Include "Entire Article Links" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeEntireArticleLinks = state => {
    return (
        getTemplateProperty(state, "article.links.linkEntireArticle") == "true"
    );
};

/**
 * Should Include "Back to Top Links" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeBackToTopLinks = state => {
    const value = getTemplateProperty(
        state,
        "article.links.backToTopLinks",
        "false"
    );
    return value == "true";
};

/**
 * Should Include "Article Tone Icon" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleToneIcon = state => {
    return getTemplateProperty(state, "misc.articleTone.include") == "icon";
};

/**
 * Should Include "Article Tone Text" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleToneText = state => {
    return getTemplateProperty(state, "misc.articleTone.include") == "text";
};

/**
 * Should Include "Article Significance" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleSignificance = state => {
    return getTemplateProperty(state, "misc.includeSignficance") == "true";
};

/**
 * Should Include "Article Primary Outlet Impressions" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticlePrimaryOutletImpressions = state => {
    return (
        getTemplateProperty(state, "misc.articleImpressions.include") ==
        "primaryOutlet"
    );
};

/**
 * Should Include "Article Group Impressions" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleGroupImpressions = state => {
    return (
        getTemplateProperty(state, "misc.articleImpressions.include") == "group"
    );
};

/**
 * Should Include "Syndication" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleSyndication = state => {
    return getTemplateProperty(state, "misc.includeSyndication") == "true";
};

/**
 * Get the "Syndication" label from the template for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getSyndicationLabel = state => {
    const value = getTemplateProperty(state, "misc.syndication.label");
    // If the value is "" or "DEFAULT" return "Syndication" instead
    const result = fixValue(value) || "Syndication";
    return result;
};

/**
 * Should Include "Article Total Social Interactions" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleTotalShares = state => {
    return getTemplateProperty(state, "misc.articleShares.include") == "total";
};

/**
 * Should Include "Social Interactions By Type" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeArticleSharesByType = state => {
    return getTemplateProperty(state, "misc.articleShares.include") == "byType";
};

/**
 * Should Include "List Top Syndicates By Sharing" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeListTopSyndicatesBySharing = state => {
    return (
        getTemplateProperty(
            state,
            "misc.syndication.listTopSyndicatesBySharing.include"
        ) == "true"
    );
};

/**
 * Should Include "List Top Syndicates By Impressions" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeListTopSyndicatesByImpressions = state => {
    return (
        getTemplateProperty(
            state,
            "misc.syndication.listTopSyndicatesByImpressions.include"
        ) == "true"
    );
};

/**
 * Should Include "Facebook" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeFacebook = state => {
    const include = String(
        getTemplateProperty(state, "interactive.includeFacebook")
    );
    return include === "true" || include === "undefined" || include === "null";
};

/**
 * Should Include "Twitter" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeTwitter = state => {
    const include = String(
        getTemplateProperty(state, "interactive.includeTwitter")
    );
    return include === "true" || include === "undefined" || include === "null";
};

/**
 * Should Include "LinkedIn" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeLinkedIn = state => {
    const include = String(
        getTemplateProperty(state, "interactive.includeLinkedIn")
    );
    return include === "true" || include === "undefined" || include === "null";
};

/**
 * Should Include "Email" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeEmail = state => {
    const include = String(
        getTemplateProperty(state, "interactive.includeEmail")
    );
    return include === "true" || include === "undefined" || include === "null";
};

/**
 * Should Include "Download Pdf" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeDownloadPdf = state => {
    const include = String(
        getTemplateProperty(state, "interactive.includeDownloadPdf")
    );
    return include === "true" || include === "undefined" || include === "null";
};

/**
 * Should Include "Copy Link" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeCopyLink = state => {
    const include = String(
        getTemplateProperty(state, "interactive.includeCopyLink")
    );
    return include === "true" || include === "undefined" || include === "null";
};

/**
 * Should Include "Capitalize Title" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeCapitalizeTitle = state => {
    return (
        getTemplateProperty(state, "article.title.sentencecase.include") ==
        "true"
    );
};

/**
 * Should Include "Email Icon" from the template for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const shouldIncludeEmailIcon = state => {
    return getTemplateProperty(state, "misc.includeEmailIcon") == "true";
};

/**
 * associated articles list label is configurable via AN-07b
 * @param {*} state
 * @returns {String} the text to display before the list of associated articles
 */
export const getAssociatedArticlesLabelText = state => {
    return getTemplateProperty(state, "article.associatedArticles.text");
};

/**
 * associated articles list label styling is configurable via AN-07b
 * @param {*} state
 * @returns {String} - styles formatted for output
 */
export const getAssociatedArticlesLabelStyling = state => {
    return getReactStyleObjectForProperty(state, "article.associatedArticles");
};

/**
 * associated articles link styling is configurable via AN-07b
 * @param {*} state
 * @returns {String} - styles formatted for output
 */
export const getAssociatedArticlesLinkStyling = state => {
    return getReactStyleObjectForProperty(
        state,
        "article.associatedArticles.links"
    );
};

/**
 * sub section heading/title style is configurable via AN-07b
 * @param {*} state
 * @returns {String} - styles formatted for output
 */
export const getSubSectionTitleStyling = state => {
    return getReactStyleObjectForProperty(state, "section.subsection.style");
};

/**
 * Newsletter display name styling
 * @param {*} state
 * @returns {String} - styles formatted for output
 */
export const getNewsletterDisplayNameStyling = state => {
    return getReactStyleObjectForProperty(state, "header.displayName");
};

/**
 * What is the spacing "font size" based on the template
 * @param state
 * @returns {*}
 */
export const getSpacingFontSize = state => {
    let returnValue;
    try {
        returnValue = parseInt(
            getTemplateProperty(state, "core.spacing.fontSize", "24")
        );
    } catch (error) {
        returnValue = getDefaultSpacingFontSize(); // defaultValue
    }
    return returnValue;
};

/**
 * What is the default spacing "font size" (hard coded)
 * @param state
 * @returns {*}
 */
export const getDefaultSpacingFontSize = () => {
    return 24;
};

/**
 * CSS string that will be applied as a rule to customize spacing
 * @param {*} state
 */
export const getSpacingStyleRules = state => {
    const isDefaultFormat = !getNewsletterUseCustomFormat(state);

    const spacingFontSize = getSpacingFontSize(state);
    const defaultFontSize = getDefaultSpacingFontSize();
    const multiplier = spacingFontSize / defaultFontSize;

    // Custom spacing style only for custom format
    const result = isDefaultFormat
        ? []
        : [
              `
        #app .newsletter-section {
            margin-top: ${64 * multiplier}px;
        }`,
              `
        @media (min-width: 768px) {
            #app .newsletter-section {
                margin-top: ${128 * multiplier}px;
            }
        }`,
              `
        #app .newsletter-sub-section {
            margin-top: ${32 * multiplier}px;
        }`,
              `
        @media (min-width: 768px) {
            #app .newsletter-sub-section {
                margin-top: ${64 * multiplier}px;
            }
        }`,
              `
        #app .article {
            margin-top: ${32 * multiplier}px;
        }`,
              `
        @media (min-width: 768px) {
            #app .article {
                margin-top: ${64 * multiplier}px;
            }
        }`,
              `
        #app .article-image {
            margin-top: ${16 * multiplier}px;
        }`,
              `
        @media (min-width: 768px) {
            #app .article-image {
                margin-top: ${32 * multiplier}px;
            }
        }`,
              `
        #app .custom-image {
            margin-top: ${16 * multiplier}px;
        }`,
              `
        @media (min-width: 768px) {
            #app .custom-image {
                margin-top: ${32 * multiplier}px;
            }
        }`,
          ];

    return result;
};

/**
 * Return the ID of the CSS rule that was injected for the purposes of spacing
 * @param state
 * @returns {Node}
 */
export const getSpacingStylesheet = state => {
    return _get(state, "styles.spacingStylesheet", null);
};
